package com.siriusxm.cmc

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.Route
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.Application
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.ApplicationDescriptor
import com.siriusxm.pia.ConsoleApplication
import com.siriusxm.pia.components.currentTimeZoneId
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Iframe
import org.w3c.dom.HTMLIFrameElement

/**
 * Renders an application by embedding an iframe. If in the same origin, the
 * iframe application will have access to window.parent.cmc, the CMCContainer,
 * which provides functionality for auth and other common operations.
 */
class IFrameApplication(val descriptor: ApplicationDescriptor) : ConsoleApplication {
    override suspend fun initialize(context: ApplicationContext) {}

    @Composable
    override fun route(route: Route) {
        var iframeEl by remember { mutableStateOf<HTMLIFrameElement?>(null) }

        // Only mutate the DOM; avoid rebuilding the <iframe> on every change
        // Use fullPath as the key since it includes query parameters
        LaunchedEffect(route.fullPath) {
            val el = iframeEl ?: return@LaunchedEffect

            // Include query parameters in the iframe URL, and always append the globally-selected
            // timezone so embedded apps can render times in the same zone as the rest of the
            // console. The IANA id (e.g. "America/New_York") contains "/", so URL-encode it. The
            // parent hard-reloads the whole page when the selection changes, which rebuilds this
            // iframe with the new value — no extra reload logic needed here.
            val baseQuery = route.fullPath.substringAfter("?", "")
            val tzParam = "timezone=${encodeURIComponent(currentTimeZoneId())}"
            val queryString = if (baseQuery.isNotEmpty()) "$baseQuery&$tzParam" else tzParam
            val pathWithQuery = "${route.remainingPath}?$queryString"

            val fullUrl = listOf(
                descriptor.source!!.removeSurrounding("/") + "/",
                pathWithQuery
            ).joinToString(separator = "#")

            // Mutating `src` on a live iframe pushes an entry onto the joint
            // session history, so each in-app navigation produced TWO history
            // entries (parent pushState + iframe src change) and Back required
            // two presses. Use location.replace() — same load, no history entry —
            // for in-place updates, falling back to setAttribute on first attach
            // when contentWindow isn't ready yet.
            val contentWindow = el.contentWindow
            val currentHref = runCatching { contentWindow?.location?.href }.getOrNull()
            if (currentHref == fullUrl) return@LaunchedEffect
            if (contentWindow != null && currentHref != null) {
                contentWindow.location.replace(fullUrl)
            } else {
                el.setAttribute("src", fullUrl)
            }

            // Reflect the active app in the document title so the history entry
            // the parent pushed reads as "<Page> — <App Name>" instead of generic
            // window.name. Browsers ignore pushState's title arg, so updating
            // document.title here is what actually labels the current entry.
            // If the iframe app called cmc.navigateWithTitle(...), the per-page
            // title was stashed on Application.pendingTitle — consume it here.
            val pageTitle = Application.pendingTitle
            Application.pendingTitle = null
            document.title = if (pageTitle != null) {
                "$pageTitle — ${descriptor.name}"
            } else {
                "${descriptor.name} — Management Console"
            }
        }

        // Keep the element stable; no conditional around Iframe
        Iframe({
            id("embedded-application")
            ref {
                iframeEl = it
                onDispose {
                    println("dispose iframe")
                }
            }
            style {
                width(100.percent)
                border(0.px)
                padding(0.px); margin(0.px)
            }
        }) {}
    }
}